import { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { Eye } from "lucide-react";
import { DEFAULT_REGISTER_PREFERENCES, REGISTER_LIST_DISPLAY_MODES } from "../constants/registerPreferences";
import { REGISTER_PARUTION_STRATA } from "../constants/registerParutionStrata";
import { REGISTER_RECENCY_STRATA } from "../constants/registerRecencyStrata";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import { filterEntriesByParutionStratum } from "../utils/registerParution";
import { filterEntriesByRecencyStratum } from "../utils/registerRecency";
import {
  readRegisterRecencyGradationSort,
  readRegisterRecencyScrollMap,
  readRegisterRecencyViewMode,
  REGISTER_GRADATION_SORT_MODES,
  REGISTER_GRADATION_SORT_OPTIONS,
  REGISTER_RECENCY_VIEW_MODES,
  writeRegisterRecencyGradationSort,
  writeRegisterRecencyScrollMap,
  writeRegisterRecencyViewMode,
} from "../utils/registerRecencyPreference";
import { getStratumGradationStats } from "../utils/registerWordListDisplay";
import { openWordInNewTab } from "../utils/registerWordLink";
import PackAssignDropdown from "./PackAssignDropdown";
import ToggleSwitch from "./ToggleSwitch";
import WordListMetaBadge from "./WordListMetaBadge";
import RegisterRecencyStrataSwitch from "./RegisterRecencyStrataSwitch";
import "./WordListBox.css";
import "./RegisterRecencyPanel.css";

function formatSharePercent(count, total) {
  if (!total) return "0,0";
  return (Math.round((count / total) * 1000) / 10).toFixed(1).replace(".", ",");
}

function getScrollKey(viewMode, stratumId) {
  return `${viewMode}:${stratumId}`;
}

export default function RegisterRecencyPanel({
  activeStratum,
  onStratumChange,
  entries = [],
  listDisplayMode = DEFAULT_REGISTER_PREFERENCES.listDisplayMode,
  showColorCode = DEFAULT_REGISTER_PREFERENCES.showColorCode,
  onSelectWord,
}) {
  const [viewMode, setViewMode] = useState(() => readRegisterRecencyViewMode());
  const [gradationSort, setGradationSort] = useState(() =>
    readRegisterRecencyGradationSort()
  );
  const listRef = useRef(null);
  const scrollPositionsRef = useRef(null);
  if (scrollPositionsRef.current === null) {
    scrollPositionsRef.current = readRegisterRecencyScrollMap();
  }
  const scrollKeyRef = useRef(getScrollKey(viewMode, activeStratum));
  const pendingRestoreRef = useRef(true);
  const persistTimerRef = useRef(null);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });
  const {
    trackRef,
    scrollbar,
    updateScrollbar,
    handleThumbPointerDown,
    handleTrackPointerDown,
  } = useCustomScrollbar(listRef);

  const isParution = viewMode === REGISTER_RECENCY_VIEW_MODES.PARUTION;
  const isGradationDisplay = listDisplayMode === REGISTER_LIST_DISPLAY_MODES.GRADATION;
  const masteredFirst = gradationSort === REGISTER_GRADATION_SORT_MODES.MASTERED_FIRST;
  const activeStrata = isParution ? REGISTER_PARUTION_STRATA : REGISTER_RECENCY_STRATA;

  const stratumEntries = useMemo(
    () =>
      isParution
        ? filterEntriesByParutionStratum(entries, activeStratum, {
            sortByGradation: isGradationDisplay,
            masteredFirst,
          })
        : filterEntriesByRecencyStratum(entries, activeStratum, {
            sortByGradation: isGradationDisplay,
            masteredFirst,
          }),
    [entries, activeStratum, isParution, isGradationDisplay, masteredFirst]
  );

  const gradeStats = useMemo(
    () => (isGradationDisplay ? getStratumGradationStats(stratumEntries) : null),
    [isGradationDisplay, stratumEntries]
  );

  const stratumMeta = useMemo(
    () => activeStrata.find((item) => item.id === activeStratum) || null,
    [activeStrata, activeStratum]
  );

  const count = stratumEntries.length;
  const total = Array.isArray(entries) ? entries.length : 0;
  const percentLabel = formatSharePercent(count, total);

  const updateScrollUi = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setScrollFades({ top: false, bottom: false });
      updateScrollbar();
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;
    const hasOverflow = scrollHeight > clientHeight + 1;

    setScrollFades({
      top: hasOverflow && scrollTop > threshold,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - threshold,
    });
    updateScrollbar();
  }, [updateScrollbar]);

  const flushScroll = useCallback(() => {
    const el = listRef.current;
    if (el && el.clientHeight > 0) {
      scrollPositionsRef.current[scrollKeyRef.current] = el.scrollTop;
    }
    writeRegisterRecencyScrollMap(scrollPositionsRef.current);
  }, []);

  const tryRestoreScroll = useCallback(() => {
    if (!pendingRestoreRef.current) return;
    const el = listRef.current;
    if (!el || el.clientHeight === 0) return;

    const saved = scrollPositionsRef.current[scrollKeyRef.current];
    el.scrollTop = typeof saved === "number" && saved > 0 ? saved : 0;
    pendingRestoreRef.current = false;
    updateScrollUi();
  }, [updateScrollUi]);

  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (el) {
      scrollPositionsRef.current[getScrollKey(viewMode, activeStratum)] = el.scrollTop;
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        writeRegisterRecencyScrollMap(scrollPositionsRef.current);
      }, 120);
    }
    updateScrollUi();
  }, [activeStratum, viewMode, updateScrollUi]);

  useEffect(() => {
    const prevKey = scrollKeyRef.current;
    const nextKey = getScrollKey(viewMode, activeStratum);
    const el = listRef.current;
    if (prevKey !== nextKey) {
      if (el && el.clientHeight > 0) {
        scrollPositionsRef.current[prevKey] = el.scrollTop;
        writeRegisterRecencyScrollMap(scrollPositionsRef.current);
      }
      scrollKeyRef.current = nextKey;
      pendingRestoreRef.current = true;
    }

    let frame1;
    let frame2;
    frame1 = requestAnimationFrame(() => {
      tryRestoreScroll();
      frame2 = requestAnimationFrame(() => tryRestoreScroll());
    });
    return () => {
      cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [activeStratum, viewMode, stratumEntries.length, tryRestoreScroll]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(() => updateScrollUi());
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollUi, stratumEntries.length]);

  useEffect(() => {
    const onBeforeUnload = () => flushScroll();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      flushScroll();
    };
  }, [flushScroll]);

  const handleSelect = (mot) => {
    onSelectWord?.(mot, "left", { userInitiated: true });
  };

  const handleOpenWord = (event, mot) => {
    event.preventDefault();
    event.stopPropagation();
    openWordInNewTab(mot);
  };

  return (
    <div className="register-recency-panel">
      <div className="register-recency-panel__stage">
        <RegisterRecencyStrataSwitch
          value={activeStratum}
          onChange={onStratumChange}
          strata={activeStrata}
          ariaLabel={
            isParution
              ? "Strates de parution des mots"
              : "Strates de récence des mots"
          }
        />
        <div
          className={[
            "register-recency-panel__list-frame",
            scrollbar.visible && "register-recency-panel__list-frame--scrollable",
            scrollFades.top && "register-recency-panel__list-frame--fade-top",
            scrollFades.bottom && "register-recency-panel__list-frame--fade-bottom",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-label={
            stratumMeta
              ? `Liste — ${stratumMeta.title}`
              : "Liste des mots de la strate sélectionnée"
          }
        >
          {stratumEntries.length === 0 ? (
            <div className="register-recency-panel__empty">
              Aucun mot dans cette strate
              {stratumMeta?.subtitle ? (
                <span className="register-recency-panel__empty-hint">
                  {stratumMeta.subtitle}
                </span>
              ) : null}
            </div>
          ) : (
            <div
              ref={listRef}
              className={`register-recency-panel__list${
                showColorCode ? "" : " listbox--no-color-code"
              }`}
              onScroll={handleListScroll}
            >
              {stratumEntries.map((entry) => {
                const mot = entry.mot;
                return (
                  <div
                    key={mot}
                    className="word-list-item"
                    onClick={() => handleSelect(mot)}
                  >
                    <span className="word-list-item__label">{mot}</span>
                    <span className="word-list-item__meta">
                      <WordListMetaBadge entry={entry} mode={listDisplayMode} />
                      <button
                        type="button"
                        className="word-list-item__eye"
                        onClick={(event) => handleOpenWord(event, mot)}
                        aria-label={`Ouvrir « ${mot} » dans un nouvel onglet`}
                      >
                        <Eye size={17} aria-hidden="true" />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div
            ref={trackRef}
            className={[
              "register-recency-panel__scrollbar",
              !scrollbar.visible && "register-recency-panel__scrollbar--hidden",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-hidden="true"
            onPointerDown={handleTrackPointerDown}
          >
            {scrollbar.visible ? (
              <div
                className="register-recency-panel__scrollbar-thumb"
                style={{
                  height: `${scrollbar.height}px`,
                  transform: `translateY(${scrollbar.top}px)`,
                }}
                onPointerDown={handleThumbPointerDown}
              />
            ) : null}
          </div>
        </div>
        <div className="register-recency-panel__footer">
          <div className="register-recency-panel__mode">
            <span className="register-recency-panel__mode-label">
              {isParution ? "Parution" : "Récence"}
            </span>
            <ToggleSwitch
              compact
              className="register-recency-panel__mode-switch"
              checked={isParution}
              uncheckedSymbol="R"
              checkedSymbol="P"
              ariaLabel={
                isParution
                  ? "Afficher les strates de récence"
                  : "Afficher les strates de parution"
              }
              onChange={(checked) => {
                const next = checked
                  ? REGISTER_RECENCY_VIEW_MODES.PARUTION
                  : REGISTER_RECENCY_VIEW_MODES.RECENCY;
                setViewMode(writeRegisterRecencyViewMode(next));
              }}
            />
            {isGradationDisplay ? (
              <div className="register-recency-panel__sort">
                <PackAssignDropdown
                  options={REGISTER_GRADATION_SORT_OPTIONS}
                  value={gradationSort}
                  onChange={(next) => {
                    setGradationSort(writeRegisterRecencyGradationSort(next));
                  }}
                  ariaLabel="Tri des mots par gradation"
                  matchTriggerWidth
                />
              </div>
            ) : null}
          </div>
          <p className="register-recency-panel__summary" aria-live="polite">
            {count.toLocaleString("fr-FR")} {count > 1 ? "mots" : "mot"}{" "}
            ({percentLabel} %)
          </p>
        </div>
        {gradeStats ? (
          <div className="register-recency-panel__grade-stats" aria-live="polite">
            <p>
              {formatSharePercent(gradeStats.masteredCount, gradeStats.total)} % de
              mots maîtrisés ({gradeStats.masteredCount.toLocaleString("fr-FR")})
            </p>
            <p>
              {formatSharePercent(gradeStats.reviewCount, gradeStats.total)} % de
              mots à revoir ({gradeStats.reviewCount.toLocaleString("fr-FR")})
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
